import { TypeReference } from '../data/type-reference.js'
import { EntitySubset } from './entity-subset.js'
import { EntityMethod } from './entity-method.js'
import type { ModuleIdType, NameAndType } from '../types.js'
import { BusinessObject } from './business-object.js'
import { EntityProperty } from './entity-property.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { RestrictableItem } from './restrictions/restrictable-item.js'
import { Parameter } from '../script/parameter.js'
import { BoVisitor } from './bo-visitor.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { EntityRelation } from './entity-relation.js'
import {
	BoSpecificDetails,
	BoSpecificDetailsQuery,
} from './bo-specific-details.js'

export const IdTypes = ['none', 'string', 'number'] as const
export type IdTypeType = (typeof IdTypes)[number]

@jsonClass('Entity')
export class Entity extends BusinessObject {
	@jsonProperty(String)
	idType: IdTypeType = 'none'

	@jsonProperty(String) baseClassModuleId?: ModuleIdType
	@jsonProperty(String) baseClassBoId?: ModuleIdType

	@jsonArrayProperty(EntityProperty) properties: EntityProperty[] = []
	@jsonArrayProperty(EntityRelation) relations: EntityRelation[] = []
	@jsonArrayProperty(EntityMethod) methods: EntityMethod[] = []
	@jsonArrayProperty(EntitySubset) subsets: EntitySubset[] = []

	readonly affectsDb: boolean = true

	constructor(init?: Partial<Entity>) {
		super()
		assignCommonProperties(this, init)
	}

	getVirtualProperties(): EntityProperty[] {
		return [
			new EntityProperty({ name: 'createdOn', type: 'Date' }),
			new EntityProperty({
				name: 'createdBy',
				type: 'System.UserIdAndName',
				questionToken: true,
				isOfInterest: false,
			}),
			new EntityProperty({ name: 'updatedOn', type: 'Date' }),
			new EntityProperty({
				name: 'updatedBy',
				type: 'System.UserIdAndName',
				questionToken: true,
				isOfInterest: false,
			}),
			new EntityProperty({ name: 'deletedOn', type: 'Date' }),
			new EntityProperty({
				name: 'deletedBy',
				type: 'System.UserIdAndName',
				questionToken: true,
				isOfInterest: false,
			}),
			new EntityProperty({ name: 'versionNumber', type: 'number' }),
		]
	}

	getAllProperties() {
		return [...this.properties, ...this.getVirtualProperties()]
	}

	getPropertiesForPropertyTree(): NameAndType[] {
		return [
			...this.getAllProperties().map((prop) => {
				const typeRef = new TypeReference(prop.type)
				return {
					name: prop.name,
					type: typeRef.getQualifiedTypeName(),
					isArray: typeRef.isArrayOrSet,
				}
			}),
			...this.relations.map((rel) => ({
				name: rel.name,
				type: rel.targetEntityRef?.getQualifiedName(),
				isArray: rel.type == 'one-to-many',
			})),
		]
	}

	getMainSubset() {
		const subset = new EntitySubset()
		this.properties.forEach((p) => subset.itemAvailability.set(p.name, 'y'))
		this.relations.forEach((r) =>
			r.getPropertyDefinitions().forEach((p) => {
				subset.itemAvailability.set(p.name, 'y')
			}),
		)
		this.methods.forEach((m) => subset.itemAvailability.set(m.name, 'y'))
		return subset
	}

	getAvailablePropertiesForSubset(subset: EntitySubset) {
		const properties = this.properties.filter(
			(p) => subset.itemAvailability.get(p.name) == 'y',
		)
		for (const relation of this.relations) {
			if (subset.itemAvailability.get(relation.name) == 'y') {
				properties.push(...relation.getPropertyDefinitions())
			}
		}
		properties.push(...this.getVirtualProperties())
		return properties
	}

	getTypeReferences() {
		const entityRef = this.getBoReference()
		return [
			new TypeReference(entityRef),
			new TypeReference(entityRef, undefined, ['Id']),
			...this.subsets.map(
				(subset) => new TypeReference(entityRef, undefined, [subset.name]),
			),
		]
	}

	getClassNames(): string[] {
		const qualifiedName = this.getQualifiedName()
		return [
			qualifiedName,
			...this.subsets.map((subset) => `${qualifiedName}__${subset.name}`),
		]
	}

	getRestrictableItems(): RestrictableItem[] {
		return this.methods
			.filter(
				(m) =>
					m.availability == 'callableFromClient' &&
					m.accessMode == 'accessControl',
			)
			.map(
				(m) =>
					new RestrictableItem({
						itemName: m.name,
						parameters: [
							// list must match what's defined in EntityApiRequest.checkAccess()
							...(m.isStatic
								? []
								: [
										new Parameter({
											name: 'clientVersion',
											type: this.getQualifiedName(),
										}),
										new Parameter({
											name: 'loadDbVersion',
											type: `() => Promise<${this.getQualifiedName()}>`,
										}),
									]),
							new Parameter({
								name: 'accessController',
								type: `EntityMethodAccessController`,
							}),
							...m.inputs.map(
								(i) => new Parameter({ name: i.name, type: i.type }),
							),
						],
						contextImports: {
							EntityMethodAccessController: '@lg/shared',
						},
						defaultCode: `\tif(Sys.User.currentUser?.hasEffectivePermission('WhicheverPermissionIsRequired')) {\n\t\taccessController.allowClient()\n\t}`,
					}),
			)
	}

	visit(visitor: BoVisitor): void {
		this.properties.forEach((property, idx) =>
			property.visit(visitor, ['properties', idx]),
		)
		this.relations.forEach((relation, idx) =>
			relation.visit(visitor, ['relations', idx]),
		)
		this.methods.forEach((method, idx) =>
			method.visit(visitor, ['method', idx]),
		)
	}

	getBoSpecificDetails(query?: BoSpecificDetailsQuery): BoSpecificDetails {
		const subTypes: Record<string, string> = { '': '(Main)' }
		for (const subset of this.subsets) {
			subTypes[subset.name] = `.${subset.name}`
		}

		return {
			...super.getBoSpecificDetails(query),
			subTypes,
		}
	}

	getIdTypeConstructor() {
		return this.idType == 'string' ? 'String' : 'Number'
	}

	getIdTypeInitializer() {
		return this.idType == 'string' ? `''` : '0'
	}
}
